/**
 * Which guides workshops actually open.
 *
 * A guide everyone opens is a screen worth simplifying; a guide nobody opens
 * is either obvious or unfindable. Either way it says more about the product
 * than about the guide.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';

const WINDOWS = [7, 30, 90];

export const GuideAnalytics = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/platform/guide-analytics`, {
        params: { days: d }, headers: authHeader(),
      });
      setData(res.data);
    } catch { setData(null); } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(days); }, [load, days]);

  const top = data?.guides?.[0]?.opens || 0;

  return (
    <section className="mt-10" data-testid="guide-analytics">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <BookOpen size={15} className="text-teal-600" /> Which guides workshops open
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {data ? `${data.total_opens} opens in the last ${data.days} days.` : 'Loading…'}{' '}
            Your own workshop is excluded. The busiest guide is usually the screen worth simplifying next.
          </p>
        </div>
        <div className="flex gap-1.5">
          {WINDOWS.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-xs font-semibold rounded-full border px-2.5 py-1 ${
                days === d ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-600 hover:border-slate-400'
              }`}
              data-testid={`guide-analytics-${d}d`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-8 grid place-items-center"><Loader2 className="animate-spin text-slate-300" size={18} /></div>
      ) : (
        <div className="mt-3 bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {(data?.guides || []).map((g) => (
            <div key={g.slug} className="px-4 py-2.5 flex items-center gap-3" data-testid={`guide-stat-${g.slug}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{g.title}</div>
                <div className="h-1 mt-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-teal-500"
                    style={{ width: top ? `${Math.round((g.opens / top) * 100)}%` : '0%' }}
                  />
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold text-slate-900 tabular-nums">{g.opens}</div>
                <div className="text-[10px] text-slate-400">
                  {g.workshops} {g.workshops === 1 ? 'workshop' : 'workshops'}
                </div>
              </div>
            </div>
          ))}
          {!data?.total_opens && (
            <p className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="guide-analytics-empty">
              No guide has been opened in this window yet.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default GuideAnalytics;
